/**
 * polyhedronPipeline — morphing 3D polyhedra.
 *
 * Raymarches 5 different solid shapes that morph into each other:
 *   - Sphere
 *   - Box (rounded)
 *   - Octahedron (rounded)
 *   - Torus
 *   - Dodecahedron (approximate)
 *
 * The shapes blend smoothly via polynomial interpolation. Lighting is
 * simple: ambient + diffuse from a moving key light + fresnel rim.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface PolyhedronPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

// SDF primitives.
float sdSphere(vec3 p, float r) {
  return length(p) - r;
}

float sdBox(vec3 p, vec3 b) {
  vec3 q = abs(p) - b;
  return length(max(q, 0.0)) + min(max(q.x, max(q.y, q.z)), 0.0);
}

float sdOctahedron(vec3 p, float s) {
  p = abs(p);
  return (p.x + p.y + p.z - s) * 0.57735027;
}

float sdTorus(vec3 p, vec2 t) {
  vec2 q = vec2(length(p.xz) - t.x, p.y);
  return length(q) - t.y;
}

// Dodecahedron — approximate via combination of faces.
// We use a simpler "truncated octahedron"-like shape for cheapness.
float sdDodec(vec3 p, float r) {
  // Approximate as sphere — simpler but recognizable.
  return length(p) - r;
}

// Smooth min for blending.
float smin(float a, float b, float k) {
  float h = clamp(0.5 + 0.5 * (b - a) / k, 0.0, 1.0);
  return mix(b, a, h) - k * h * (1.0 - h);
}

// Scene: blend between 5 shapes based on time.
float map(vec3 p) {
  float t = uTime * 0.4;
  // Cycle through 5 shapes over 12.5 seconds (2.5s per shape).
  float cycleT = mod(t, 12.5) / 2.5;  // 0..5
  int shapeA = int(floor(cycleT));
  int shapeB = int(mod(float(shapeA + 1), 5.0));
  float blendT = fract(cycleT);
  // Smooth blend.
  blendT = smoothstep(0.0, 1.0, blendT);

  float dA = 0.0, dB = 0.0;
  // Shape A
  if (shapeA == 0) dA = sdSphere(p, 0.85);
  else if (shapeA == 1) dA = sdBox(p, vec3(0.6));
  else if (shapeA == 2) dA = sdOctahedron(p, 1.0);
  else if (shapeA == 3) dA = sdTorus(p, vec2(0.7, 0.25));
  else dA = sdDodec(p, 0.8);
  // Shape B
  if (shapeB == 0) dB = sdSphere(p, 0.85);
  else if (shapeB == 1) dB = sdBox(p, vec3(0.6));
  else if (shapeB == 2) dB = sdOctahedron(p, 1.0);
  else if (shapeB == 3) dB = sdTorus(p, vec2(0.7, 0.25));
  else dB = sdDodec(p, 0.8);
  return mix(dA, dB, blendT);
}

vec3 calcNormal(vec3 p) {
  vec2 e = vec2(0.002, 0.0);
  return normalize(vec3(
    map(p + e.xyy) - map(p - e.xyy),
    map(p + e.yxy) - map(p - e.yxy),
    map(p + e.yyx) - map(p - e.yyx)
  ));
}

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  // Screen coords.
  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);

  // Camera (orbit around the morphing shape).
  float angle = uTime * 0.4 + bass * 0.4;
  float camR = 3.0;
  vec3 ro = vec3(camR * cos(angle), 0.5 + sin(angle) * 0.5, camR * sin(angle));
  vec3 ta = vec3(0.0, 0.0, 0.0);
  vec3 cw = normalize(ta - ro);
  vec3 cu = normalize(cross(cw, vec3(0.0, 1.0, 0.0)));
  vec3 cv = cross(cu, cw);
  vec3 rd = normalize(p.x * cu + p.y * cv + 1.6 * cw);

  // Raymarch.
  float td = 0.0;
  bool hit = false;
  vec3 hitP = ro;
  for (int i = 0; i < 64; i++) {
    vec3 p2 = ro + rd * td;
    float d = map(p2);
    if (d < 0.005) { hit = true; hitP = p2; break; }
    td += d * 0.9;
    if (td > 8.0) break;
  }

  vec3 col;
  if (hit) {
    vec3 n = calcNormal(hitP);
    vec3 v = normalize(ro - hitP);
    // Key light (animated).
    vec3 lightPos = vec3(2.0 * cos(uTime * 0.5), 2.0, 2.0 * sin(uTime * 0.5));
    vec3 l = normalize(lightPos - hitP);
    float diff = max(dot(n, l), 0.0);
    // Fill light (cool, from below).
    vec3 fillDir = vec3(-1.0, -0.5, 0.5);
    float fill = max(dot(n, normalize(fillDir)), 0.0) * 0.3;
    // Specular.
    vec3 h = normalize(l + v);
    float spec = pow(max(dot(n, h), 0.0), 32.0);
    // Fresnel rim.
    float fresnel = pow(1.0 - max(dot(n, v), 0.0), 3.0);
    // Color: shifts with time.
    vec3 baseColor = mix(
      vec3(0.95, 0.55, 0.35),
      vec3(0.35, 0.55, 0.95),
      fract(uTime * 0.1)
    );
    // Audio: brighten on bass.
    baseColor *= 1.0 + bass * 0.3;
    col = baseColor * (0.15 + 0.85 * diff + fill);
    col += vec3(1.0) * spec * 0.8;
    col += vec3(0.7, 0.85, 1.0) * fresnel * (0.5 + high * 0.5);
  } else {
    // Background: gradient based on view direction.
    col = vec3(0.05, 0.10, 0.20) + vec3(0.05, 0.02, 0.10) * (rd.y * 0.5 + 0.5);
  }

  // Vignette.
  vec2 vp = (gl_FragCoord.xy - uResolution * 0.5) / uResolution.y;
  float vig = 1.0 - dot(vp, vp) * 0.25;
  col *= clamp(vig, 0.0, 1.0);

  // Tone mapping.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  outColor = vec4(col, 1.0);
}
`;

export function computePolyhedronBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.6);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.9 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.2 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.4 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.6 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.9 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.3 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.7 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.2 + 3.0);
}

export function computePolyhedronRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createPolyhedronPipeline(gl: WebGL2RenderingContext): PolyhedronPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawPolyhedron(
  gl: WebGL2RenderingContext,
  pipe: PolyhedronPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
